import { defineStore } from 'pinia';
import { LabDetailedAnalyzer } from '../analysis/modules/LabDetailedAnalyzer';
import { HslDetailedAnalyzer } from '../analysis/modules/HslDetailedAnalyzer';
import {
    createDefaultAnalysisBootstrapState,
    deriveAnalysisBootstrapState,
    toUint8Array
} from './imageStoreShared';
import { useImageAssetStore } from './useImageAssetStore';

const createEmptyImageInfo = () => ({
    width: 0,
    height: 0,
    totalPixels: 0
});

const createGrayscaleState = () => ({
    original: null,
    processed: null,
    reference: null
});

const createStatisticsState = () => ({
    original: {},
    processed: {},
    reference: {}
});

const createDetailedAnalysisState = () => ({
    original: {},
    processed: {},
    reference: {}
});

const createColorPaletteState = () => ({
    original: null,
    processed: null,
    reference: null
});

const createRegionMasksState = () => ({
    original: {},
    processed: {}
});

export const useImageAnalysisStore = defineStore('imageAnalysis', {
    state: () => ({
        opencvLoaded: false,
        useBorderCropped: true,
        borderInfo: null,
        originalAnalysisData: null,
        processedAnalysisData: null,
        imageInfo: createEmptyImageInfo(),
        zoneAnalysis: null,
        graynessAnalysis: null,
        brightnessAnalysis: null,
        lightnessAnalysis: null,
        luminosityAnalysis: null,
        rgbAnalysis: null,
        redAnalysis: null,
        greenAnalysis: null,
        blueAnalysis: null,
        yAxisMax: 0,
        rgbDistribution: null,
        luminosityDistribution: null,
        grayDistribution: null,
        redDistribution: null,
        greenDistribution: null,
        blueDistribution: null,
        totalPixels: 0,
        grayscaleData: createGrayscaleState(),
        labStatistics: createStatisticsState(),
        hslStatistics: createStatisticsState(),
        selectedZone: null,
        labDetailedAnalysis: createDetailedAnalysisState(),
        hslDetailedAnalysis: createDetailedAnalysisState(),
        currentLanguage: 'en',
        colorPalette: createColorPaletteState(),
        regionMasks: createRegionMasksState(),
        analysisBootstrapState: createDefaultAnalysisBootstrapState(),
        labDetailedAnalyzer: null,
        hslDetailedAnalyzer: null
    }),

    getters: {
        currentAnalysisData: (state) => {
            return state.useBorderCropped ? state.processedAnalysisData : state.originalAnalysisData;
        },

        hasBothResults: (state) => {
            return Boolean(state.originalAnalysisData && state.processedAnalysisData);
        }
    },

    actions: {
        setOpencvLoaded(value) {
            this.opencvLoaded = Boolean(value);
        },

        setAnalysisResults(original, processed) {
            this.originalAnalysisData = original || { rgbDistribution: {}, redDistribution: {}, greenDistribution: {}, blueDistribution: {} };
            this.processedAnalysisData = processed || this.originalAnalysisData;
            this.updateCurrentAnalysis();
        },

        setUseBorderCropped(value) {
            this.useBorderCropped = value;
            this.updateCurrentAnalysis();
        },

        updateCurrentAnalysis() {
            const currentData = this.useBorderCropped ? this.processedAnalysisData : this.originalAnalysisData;
            if (!currentData) {
                return;
            }

            if (currentData.zoneAnalysis) {
                this.zoneAnalysis = currentData.zoneAnalysis;
            }

            this.graynessAnalysis = currentData.graynessAnalysis;
            this.brightnessAnalysis = currentData.brightnessAnalysis;
            this.lightnessAnalysis = currentData.lightnessAnalysis;
            this.luminosityAnalysis = currentData.luminosityAnalysis;
            this.rgbAnalysis = currentData.rgbAnalysis;
            this.redAnalysis = currentData.redAnalysis;
            this.greenAnalysis = currentData.greenAnalysis;
            this.blueAnalysis = currentData.blueAnalysis;
            this.rgbDistribution = currentData.rgbDistribution;
            this.luminosityDistribution = currentData.luminosityDistribution;
            this.grayDistribution = currentData.grayDistribution;
            this.redDistribution = currentData.redDistribution;
            this.greenDistribution = currentData.greenDistribution;
            this.blueDistribution = currentData.blueDistribution;
            this.imageInfo = currentData.imageInfo || {
                width: 0,
                height: 0,
                totalPixels: currentData.totalPixels || 0
            };
            this.updateYAxisMax(currentData.rgbAnalysis);

            if (currentData.rgbDistribution) {
                this.rgbDistribution = {
                    ...currentData.rgbDistribution,
                    zoneAnalysis: currentData.rgbDistribution.zoneAnalysis
                };
            }

            if (currentData.redDistribution) {
                this.redDistribution = {
                    ...currentData.redDistribution,
                    zoneAnalysis: currentData.redDistribution.zoneAnalysis
                };
            }

            if (currentData.greenDistribution) {
                this.greenDistribution = {
                    ...currentData.greenDistribution,
                    zoneAnalysis: currentData.greenDistribution.zoneAnalysis
                };
            }

            if (currentData.blueDistribution) {
                this.blueDistribution = {
                    ...currentData.blueDistribution,
                    zoneAnalysis: currentData.blueDistribution.zoneAnalysis
                };
            }

            if (currentData.labStatistics) {
                this.labStatistics = currentData.labStatistics;
            }

            if (currentData.colorPalette) {
                this.colorPalette = currentData.colorPalette;
            }
        },

        setBorderInfo(data) {
            this.borderInfo = data;
        },

        resetAnalysisResults() {
            this.borderInfo = null;
            this.originalAnalysisData = null;
            this.processedAnalysisData = null;
            this.zoneAnalysis = null;
            this.graynessAnalysis = null;
            this.brightnessAnalysis = null;
            this.lightnessAnalysis = null;
            this.luminosityAnalysis = null;
            this.rgbAnalysis = null;
            this.redAnalysis = null;
            this.greenAnalysis = null;
            this.blueAnalysis = null;
            this.rgbDistribution = null;
            this.luminosityDistribution = null;
            this.grayDistribution = null;
            this.redDistribution = null;
            this.greenDistribution = null;
            this.blueDistribution = null;
            this.imageInfo = createEmptyImageInfo();
            this.totalPixels = 0;
            this.yAxisMax = 0;
            this.grayscaleData = createGrayscaleState();
            this.labStatistics = createStatisticsState();
            this.hslStatistics = createStatisticsState();
            this.labDetailedAnalysis = createDetailedAnalysisState();
            this.hslDetailedAnalysis = createDetailedAnalysisState();
            this.selectedZone = null;
            this.colorPalette = createColorPaletteState();
            this.regionMasks = createRegionMasksState();
        },

        resetAnalysisSession() {
            this.resetAnalysisResults();
            this.resetAnalysisBootstrapState();
            this.opencvLoaded = false;
            this.useBorderCropped = true;
        },

        setGraynessAnalysis(data) {
            this.graynessAnalysis = data;
            this.updateYAxisMax(data);
        },

        setBrightnessAnalysis(data) {
            this.brightnessAnalysis = data;
            this.updateYAxisMax(data);
        },

        setLightnessAnalysis(data) {
            this.lightnessAnalysis = data;
            this.updateYAxisMax(data);
        },

        setLuminosityAnalysis(data) {
            this.luminosityAnalysis = data;
            this.updateYAxisMax(data);
        },

        setRGBAnalysis(data) {
            this.rgbAnalysis = data;
            this.updateYAxisMax(data);
        },

        setRedAnalysis(data) {
            this.redAnalysis = data;
            this.updateYAxisMax(data);
        },

        setGreenAnalysis(data) {
            this.greenAnalysis = data;
            this.updateYAxisMax(data);
        },

        setBlueAnalysis(data) {
            this.blueAnalysis = data;
            this.updateYAxisMax(data);
        },

        setRGBDistribution(data) {
            if (!data) {
                this.rgbDistribution = null;
                return;
            }

            const processedData = this.processDistributionData(data);
            this.rgbDistribution = {
                ...processedData,
                totalPixels: this.totalPixels
            };
        },

        updateYAxisMax(newData) {
            if (newData && newData.length > 0) {
                const maxValue = Math.max(...newData);
                this.yAxisMax = Math.max(this.yAxisMax, maxValue);
            }
        },

        setYAxisMax(value) {
            this.yAxisMax = value;
        },

        setLuminosityDistribution(data) {
            if (!data) {
                this.luminosityDistribution = null;
                return;
            }

            this.luminosityDistribution = this.processDistributionData(data);
        },

        setGrayDistribution(data) {
            this.grayDistribution = this.processDistributionData(data);
        },

        setRedDistribution(data) {
            if (!data) {
                this.redDistribution = null;
                return;
            }
            this.redDistribution = this.processDistributionData(data);
        },

        setGreenDistribution(data) {
            if (!data) {
                this.greenDistribution = null;
                return;
            }
            this.greenDistribution = this.processDistributionData(data);
        },

        setBlueDistribution(data) {
            if (!data) {
                this.blueDistribution = null;
                return;
            }
            this.blueDistribution = this.processDistributionData(data);
        },

        processDistributionData(data) {
            return {
                ...data,
                span: data.span ? {
                    ...data.span,
                    threshold: Number(data.span.threshold),
                    ratio: Number(data.span.ratio || 0.7),
                    maxConsecutive: Number(data.span.maxConsecutive),
                    hitSteps: [...data.span.hitSteps]
                } : null,
                zones: data.zones ? {
                    ...data.zones,
                    totalAverage: Number(data.zones.totalAverage),
                    blacks: {
                        ...data.zones.blacks,
                        average: Number(data.zones.blacks.average)
                    },
                    shadows: {
                        ...data.zones.shadows,
                        average: Number(data.zones.shadows.average)
                    },
                    midtones: {
                        ...data.zones.midtones,
                        average: Number(data.zones.midtones.average)
                    },
                    highlights: {
                        ...data.zones.highlights,
                        average: Number(data.zones.highlights.average)
                    },
                    whites: {
                        ...data.zones.whites,
                        average: Number(data.zones.whites.average)
                    }
                } : null
            };
        },

        setTotalPixels(value) {
            this.totalPixels = value;
        },

        setGrayscaleData(data, type = 'original') {
            try {
                const width = Number(data?.width || 0);
                const height = Number(data?.height || 0);
                const typedData = toUint8Array(data?.data);
                if (!typedData || !width || !height || typedData.length !== width * height) {
                    console.warn('Store: 灰度图数据无效，跳过写入', { type });
                    return;
                }

                this.grayscaleData[type] = {
                    data: typedData,
                    width,
                    height
                };
            } catch (error) {
                console.error('Store: 设置灰度图数据时出错:', error);
            }
        },

        getGrayscaleData(type = 'original') {
            return this.grayscaleData[type];
        },

        setSelectedZone(zoneType) {
            this.selectedZone = zoneType;
        },

        setLabStatisticsResults(analysisType, data) {
            if (analysisType === 'reference') {
                this.labStatistics.reference = data && typeof data === 'object'
                    ? { ...data }
                    : {};
                return;
            }

            this.labStatistics[analysisType] = {
                ...this.labStatistics[analysisType],
                ...data
            };
        },

        setHslStatisticsResults(analysisType, data) {
            if (analysisType === 'reference') {
                this.hslStatistics.reference = data && typeof data === 'object'
                    ? { ...data }
                    : {};
                return;
            }

            this.hslStatistics[analysisType] = {
                ...this.hslStatistics[analysisType],
                ...data
            };
        },

        getLabStatistics(analysisType = 'original', zoneName = 'global') {
            const analysisData = this.labStatistics[analysisType];
            if (!analysisData) {
                return null;
            }
            return analysisData[zoneName] || null;
        },

        getHslStatistics(analysisType = 'original', zoneName = 'global') {
            const analysisData = this.hslStatistics[analysisType];
            if (!analysisData) {
                return null;
            }
            return analysisData[zoneName] || null;
        },

        setLabDetailedAnalysis(analysisType, data) {
            if (data?.analyzer instanceof LabDetailedAnalyzer) {
                this.labDetailedAnalyzer = data.analyzer;
                this.labDetailedAnalyzer.setLanguage(this.currentLanguage);
            }

            if (!this.labDetailedAnalyzer) {
                this.labDetailedAnalyzer = new LabDetailedAnalyzer(this.currentLanguage);
            }

            this.labDetailedAnalysis[analysisType] = data;
        },

        getLabDetailedAnalysis(analysisType = 'original') {
            return this.labDetailedAnalysis[analysisType];
        },

        setHslDetailedAnalysis(analysisType, data) {
            if (data?.analyzer instanceof HslDetailedAnalyzer) {
                this.hslDetailedAnalyzer = data.analyzer;
                this.hslDetailedAnalyzer.setLanguage(this.currentLanguage);
            }

            if (!this.hslDetailedAnalyzer) {
                this.hslDetailedAnalyzer = new HslDetailedAnalyzer(this.currentLanguage);
            }

            this.hslDetailedAnalysis[analysisType] = data;
        },

        getHslDetailedAnalysis(analysisType = 'original') {
            return this.hslDetailedAnalysis[analysisType];
        },

        resetAnalysisBootstrapState() {
            this.analysisBootstrapState = createDefaultAnalysisBootstrapState();
        },

        setAnalysisBootstrapState(patch = {}) {
            const assetStore = useImageAssetStore();
            const current = this.analysisBootstrapState || createDefaultAnalysisBootstrapState();
            const merged = {
                ...current,
                ...(patch || {})
            };
            this.analysisBootstrapState = deriveAnalysisBootstrapState(merged, {
                originalHashId: assetStore.imageIds?.original || null,
                processedHashId: assetStore.imageIds?.processed || null
            });
        },

        markAnalysisBootstrapAnalyzing() {
            this.setAnalysisBootstrapState({
                status: 'analyzing',
                originalReady: false,
                cropDecisionReady: false,
                croppingDetected: false,
                processedReady: false,
                readyForExecution: false,
                borderInfo: null,
                errorCode: null
            });
        },

        markAnalysisBootstrapCropDeciding() {
            this.setAnalysisBootstrapState({
                status: 'crop_deciding',
                cropDecisionReady: false,
                borderInfo: null
            });
        },

        markAnalysisBootstrapOriginalReady() {
            this.setAnalysisBootstrapState({
                originalReady: true
            });
        },

        markAnalysisBootstrapCropDecision({ croppingDetected = false, borderInfo = null } = {}) {
            this.setAnalysisBootstrapState({
                status: 'crop_deciding',
                cropDecisionReady: true,
                croppingDetected: Boolean(croppingDetected),
                borderInfo: borderInfo || null
            });
        },

        markAnalysisBootstrapProcessedReady(processedHashId = null) {
            const assetStore = useImageAssetStore();
            if (processedHashId) {
                assetStore.imageIds.processed = processedHashId;
            }
            this.setAnalysisBootstrapState({
                processedReady: true
            });
        },

        markAnalysisBootstrapError(errorCode = 'analysis_bootstrap_failed') {
            this.setAnalysisBootstrapState({
                status: 'error',
                errorCode
            });
        },

        setReferenceCoreMirror(core) {
            const normalizedCore = core && typeof core === 'object' ? core : null;

            this.setLabStatisticsResults('reference', normalizedCore?.labStatistics || null);
            this.setHslStatisticsResults('reference', normalizedCore?.hslStatistics || null);
            this.labDetailedAnalysis.reference = normalizedCore?.labDetailedAnalysis || null;

            let referenceHslDetailed = normalizedCore?.hslDetailedAnalysis || null;
            if (!referenceHslDetailed && normalizedCore?.hslStatistics) {
                try {
                    if (!this.hslDetailedAnalyzer) {
                        this.hslDetailedAnalyzer = new HslDetailedAnalyzer(this.currentLanguage);
                    }
                    referenceHslDetailed = this.hslDetailedAnalyzer.analyze(normalizedCore.hslStatistics);
                } catch (error) {
                    console.warn('Store: reference HSL detailed 回填失败，保持空值', error);
                }
            }

            this.hslDetailedAnalysis.reference = referenceHslDetailed || null;
            this.colorPalette.reference = normalizedCore?.colorPalette || null;
        },

        syncReferenceAnalysisStats(core) {
            this.setReferenceCoreMirror(core);
        },

        hydrateReferenceMaskArtifacts({ core = null, evidence = null } = {}) {
            const analysisType = 'reference';
            this.grayscaleData[analysisType] = null;

            const grayscale = core?.grayscaleData || evidence?.grayscaleData || null;
            if (grayscale?.data && grayscale?.width && grayscale?.height) {
                this.setGrayscaleData({
                    data: grayscale.data instanceof Uint8Array ? grayscale.data : new Uint8Array(grayscale.data),
                    width: Number(grayscale.width),
                    height: Number(grayscale.height)
                }, analysisType);
            }
            // Step D：取消 25 张预生成 Mask，reference 不再把逐像素 zoneMasks/rgbMasks 复制进 Pinia；
            // 比较算法仅使用直方图与 zoneAnalysis.proportions 等摘要，Mask 预览按 image.src + selector 按需渲染。
        },

        clearReferenceArtifacts() {
            this.labStatistics.reference = null;
            this.hslStatistics.reference = null;
            this.labDetailedAnalysis.reference = null;
            this.hslDetailedAnalysis.reference = null;
            this.grayscaleData.reference = null;
            this.colorPalette.reference = null;
        },

        setLanguage(lang) {
            if (lang !== 'zh' && lang !== 'en') {
                lang = 'zh';
            }

            this.currentLanguage = lang;

            if (!this.labDetailedAnalyzer) {
                this.labDetailedAnalyzer = new LabDetailedAnalyzer(lang);
            } else {
                this.labDetailedAnalyzer.setLanguage(lang);
            }

            if (!this.hslDetailedAnalyzer) {
                this.hslDetailedAnalyzer = new HslDetailedAnalyzer(lang);
            } else {
                this.hslDetailedAnalyzer.setLanguage(lang);
            }

            if (this.labDetailedAnalysis.original) {
                const originalResults = this.regenerateAnalysisResults(this.labDetailedAnalysis.original);
                this.setLabDetailedAnalysis('original', originalResults);
            }

            if (this.labDetailedAnalysis.processed) {
                const processedResults = this.regenerateAnalysisResults(this.labDetailedAnalysis.processed);
                this.setLabDetailedAnalysis('processed', processedResults);
            }

            if (this.labDetailedAnalysis.reference) {
                const referenceResults = this.regenerateAnalysisResults(this.labDetailedAnalysis.reference);
                this.setLabDetailedAnalysis('reference', referenceResults);
            }

            if (this.hslDetailedAnalysis.original) {
                const originalResults = this.regenerateHslAnalysisResults(this.hslDetailedAnalysis.original);
                this.setHslDetailedAnalysis('original', originalResults);
            }

            if (this.hslDetailedAnalysis.processed) {
                const processedResults = this.regenerateHslAnalysisResults(this.hslDetailedAnalysis.processed);
                this.setHslDetailedAnalysis('processed', processedResults);
            }

            if (this.hslDetailedAnalysis.reference) {
                const referenceResults = this.regenerateHslAnalysisResults(this.hslDetailedAnalysis.reference);
                this.setHslDetailedAnalysis('reference', referenceResults);
            }
        },

        regenerateAnalysisResults(existingResults) {
            if (!existingResults?.structuredConclusions?.global?.colorTemperature?.statistics) {
                return existingResults;
            }

            const newResults = {
                ...existingResults,
                global: {
                    ...existingResults.global,
                    conclusions: this.labDetailedAnalyzer.generateAnalysisConclusion(
                        existingResults.global,
                        'global'
                    ).conclusions
                },
                partitions: {},
                conclusions: {
                    global: null,
                    partitions: {},
                    overall: null
                }
            };

            Object.entries(existingResults.partitions).forEach(([name, stats]) => {
                if (!stats?.structuredConclusions?.colorTemperature?.statistics) {
                    newResults.partitions[name] = stats;
                    return;
                }

                newResults.partitions[name] = {
                    ...stats,
                    conclusions: this.labDetailedAnalyzer.generateAnalysisConclusion(
                        stats,
                        name
                    ).conclusions
                };
            });

            try {
                newResults.conclusions.overall = this.labDetailedAnalyzer.conclusionGenerator.generateOverallConclusion(
                    newResults.structuredConclusions.global,
                    newResults.structuredConclusions.partitions
                );
            } catch (error) {
                console.error('Store: 生成总体结论时出错:', error);
                newResults.conclusions.overall = '分析结论生成失败';
            }

            return newResults;
        },

        regenerateHslAnalysisResults(existingResults) {
            if (!this.hslDetailedAnalyzer) {
                this.hslDetailedAnalyzer = new HslDetailedAnalyzer(this.currentLanguage);
            }

            if (!existingResults?.global?.h?.statistics) {
                return existingResults;
            }

            const newResults = {
                ...existingResults,
                global: {
                    ...existingResults.global,
                    conclusions: this.hslDetailedAnalyzer.generateAnalysisConclusion(
                        existingResults.global,
                        'global'
                    ).conclusions
                },
                partitions: {},
                conclusions: {
                    global: null,
                    partitions: {},
                    overall: null
                }
            };

            Object.entries(existingResults.partitions || {}).forEach(([name, stats]) => {
                if (!stats?.h?.statistics) {
                    newResults.partitions[name] = stats;
                    return;
                }

                newResults.partitions[name] = {
                    ...stats,
                    conclusions: this.hslDetailedAnalyzer.generateAnalysisConclusion(
                        stats,
                        name
                    ).conclusions
                };
            });

            try {
                newResults.conclusions.overall = this.hslDetailedAnalyzer.conclusionGenerator.generateOverallConclusion(
                    newResults.structuredConclusions?.global,
                    newResults.structuredConclusions?.partitions
                );
            } catch (error) {
                console.error('Store: 生成HSL总体结论时出错:', error);
                newResults.conclusions.overall = '分析结论生成失败';
            }

            return newResults;
        },

        setColorPaletteResults(data, analysisType = 'original') {
            if (!data) {
                this.colorPalette[analysisType] = null;
                return;
            }

            try {
                if (data.global || data.dominantColors) {
                    const globalData = data.global || data || {
                        dominantColors: [],
                        statistics: {
                            colorCount: 0,
                            mainColorProportion: 0,
                            colorfulness: 0
                        }
                    };

                    this.colorPalette[analysisType] = {
                        global: {
                            dominantColors: globalData.dominantColors || [],
                            distribution: globalData.distribution || null,
                            harmony: globalData.harmony || null,
                            statistics: globalData.statistics || {
                                colorCount: 0,
                                mainColorProportion: 0,
                                colorfulness: 0
                            }
                        }
                    };
                } else {
                    this.colorPalette[analysisType] = {
                        global: {
                            dominantColors: [],
                            distribution: null,
                            harmony: null,
                            statistics: {
                                colorCount: 0,
                                mainColorProportion: 0,
                                colorfulness: 0
                            }
                        }
                    };
                }
            } catch (error) {
                console.error('Store: 处理颜色分析数据时出错:', error);
                this.colorPalette[analysisType] = null;
            }
        },

        setRegionMask(maskData, analysisType = 'original') {
            try {
                if (!this.regionMasks[analysisType]) {
                    this.regionMasks[analysisType] = {};
                }

                this.regionMasks[analysisType][maskData.region] = {
                    data: maskData.data,
                    width: maskData.width,
                    height: maskData.height,
                    region: maskData.region
                };
            } catch (error) {
                console.error('Store: setRegionMask failed:', error);
            }
        },

        getRegionMask(region, analysisType = 'original') {
            return this.regionMasks[analysisType]?.[region] || null;
        }
    }
});
